import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import axiosInstance from '../lib/axios';

interface ApiResponse<T = unknown> {
  success: boolean;
  data?: T;
  message?: string;
  error?: string;
}

export interface AnonymousLimitConfig {
  maxMessagesPerSession: number;
  maxChatSessionsPerDay: number;
  maxFilesPerDay: number;
  maxImagesPerDay: number;
  maxFileBytesPerDay: number;
  maxCreditsPerDay: number;
  maxRequestsPerMinute: number;
}

export interface AnonymousLimitResponse {
  config: AnonymousLimitConfig;
  freeRegisteredTokenLimit: number | null;
}

export const anonymousLimitKeys = {
  all: ['anonymous-limits'] as const,
  detail: () => [...anonymousLimitKeys.all, 'detail'] as const,
};

const fetchAnonymousLimits = async (): Promise<AnonymousLimitResponse> => {
  const response = await axiosInstance.get<ApiResponse<AnonymousLimitResponse>>(
    '/v1/admin/anonymous-limits'
  );

  if (!response.data.data) {
    throw new Error('Anonymous limits response is empty');
  }

  return response.data.data;
};

const updateAnonymousLimits = async (
  config: AnonymousLimitConfig
): Promise<AnonymousLimitResponse> => {
  const response = await axiosInstance.put<ApiResponse<AnonymousLimitResponse>>(
    '/v1/admin/anonymous-limits',
    config
  );

  if (!response.data.data) {
    throw new Error(response.data.message || 'Anonymous limits response is empty');
  }

  return response.data.data;
};

export const useAnonymousLimits = () => {
  return useQuery({
    queryKey: anonymousLimitKeys.detail(),
    queryFn: fetchAnonymousLimits,
    staleTime: 60 * 1000,
  });
};

export const useUpdateAnonymousLimits = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: updateAnonymousLimits,
    onSuccess: (data) => {
      queryClient.setQueryData(anonymousLimitKeys.detail(), data);
      queryClient.invalidateQueries({ queryKey: anonymousLimitKeys.detail() });
    },
  });
};
